import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { FormConfig } from '@workwave-tidal/core/form'
import { form } from './formState'

import { selectCompanies, selectCompaniesAsArray } from '@/features/domain/company'
import { selectTerritories } from '@/features/domain/territory'

import { LoadingTerritoriesData } from '../components/LoadingTerritoriesData'
import { LoadingTerritoriesDataError } from '../components/LoadingTerritoriesDataError'
import { Footer } from './components/Footer'
import { FormContent } from './components/Form'

import { useFetchTerritoriesDriversAndVehicles } from '../hooks/useFetchTerritoriesDriversAndVehicles'
import { useTerritoryNames } from '../hooks/useTerritoryNames'
import { useOnSubmit } from './hooks/useOnSubmit/useOnSubmit'
import { useTexts } from './hooks/useTexts'

import type { SingleEditProps as Props, UserFormValues } from './types'
import { computeFormInitialValues } from './utils/computeFormInitialValues'

export function SingleEdit(props: Props) {
  const territoriesData = useFetchTerritoriesDriversAndVehicles()
  const territoryNames = useTerritoryNames()
  const territories = useSelector(selectTerritories)
  const companies = useSelector(selectCompanies)
  const allCompaniesCount = useSelector(selectCompaniesAsArray).length

  const texts = useTexts()

  const { onSubmit } = useOnSubmit(props.creating)

  const config = useMemo<FormConfig<UserFormValues>>(
    () => ({
      id: 'user-form',
      initialValues: computeFormInitialValues(props, territories, companies, allCompaniesCount),
    }),
    [props, territories, companies],
  )

  return (
    <>
      {territoriesData.status === 'error' && <LoadingTerritoriesDataError />}
      {territoriesData.status === 'loading' && <LoadingTerritoriesData />}
      {territoriesData.status === 'ready' && (
        <form.Form
          title={props.creating ? texts.titleCreating : texts.titleEditing}
          footer={<Footer />}
          onSubmit={onSubmit}
          config={config}
          requireTransaction={false}
          stopEditingOnUnmount
          applyPendingFitMapOnMount={false}
        >
          <FormContent
            {...props}
            territoriesData={territoriesData.data}
            territoryNames={territoryNames}
          />
        </form.Form>
      )}
    </>
  )
}
